import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Users,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Mail,
  Phone,
  Home,
  Calendar,
  ChevronRight,
  UserCheck,
  UserX,
  Clock,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { tenantApi, Tenant } from '@/lib/api';
import AddTenantDialog from '@/components/tenants/AddTenantDialog';
import toast from 'react-hot-toast';

export default function TenantsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const { data: tenants = [], isLoading, error } = useQuery({
    queryKey: ['tenants'],
    queryFn: async () => {
      const response = await tenantApi.getAll();
      return response.data;
    },
  });

  const filteredTenants = tenants.filter((tenant) => {
    const fullName = `${tenant.firstName} ${tenant.lastName}`.toLowerCase();
    const matchesSearch =
      fullName.includes(searchQuery.toLowerCase()) ||
      tenant.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (tenant.phone?.includes(searchQuery) ?? false);
    const matchesStatus =
      statusFilter === 'all' ||
      tenant.status.toLowerCase() === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  const activeTenants = tenants.filter((t) => t.status === 'Active').length;
  const applicants = tenants.filter((t) => t.status === 'Applicant').length;
  const formerTenants = tenants.filter((t) => t.status === 'Former').length;

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          <p className="text-gray-500">Loading tenants...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center">
          <AlertCircle className="h-12 w-12 text-red-500" />
          <h3 className="text-lg font-medium text-gray-900">Failed to load tenants</h3>
          <p className="text-sm text-gray-500">Please try again later</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tenants</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your tenants and applicants
          </p>
        </div>
        <Button className="gap-2" onClick={() => setIsAddDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Add Tenant
        </Button>
      </div>

      {/* Add Tenant Dialog */}
      <AddTenantDialog
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
      />

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          title="Active Tenants"
          value={activeTenants.toString()}
          icon={UserCheck}
          color="green"
        />
        <StatCard
          title="Applicants"
          value={applicants.toString()}
          icon={Clock}
          color="amber"
        />
        <StatCard
          title="Former Tenants"
          value={formerTenants.toString()}
          icon={UserX}
          color="gray"
        />
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col gap-4 rounded-xl bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search tenants by name, email, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-10 w-full rounded-lg border border-gray-200 bg-gray-50 pl-10 pr-4 text-sm transition-all focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          />
        </div>

        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="applicant">Applicants</option>
            <option value="former">Former</option>
          </select>

          <Button variant="outline" size="icon">
            <Filter className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tenants List */}
      <div className="space-y-3">
        {filteredTenants.map((tenant) => (
          <TenantCard key={tenant.id} tenant={tenant} />
        ))}
      </div>

      {filteredTenants.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl bg-white py-16 text-center shadow-sm">
          <Users className="h-12 w-12 text-gray-300" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No tenants found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchQuery
              ? 'Try adjusting your search or filters'
              : 'Get started by adding your first tenant'}
          </p>
          {!searchQuery && (
            <Button className="mt-4 gap-2">
              <Plus className="h-4 w-4" />
              Add Tenant
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// Stat Card Component
interface StatCardProps {
  title: string;
  value: string;
  icon: React.ElementType;
  color: 'green' | 'amber' | 'gray';
}

function StatCard({ title, value, icon: Icon, color }: StatCardProps) {
  const colorClasses = {
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    gray: 'bg-gray-100 text-gray-600',
  };

  return (
    <div className="rounded-xl bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={cn('rounded-lg p-2.5', colorClasses[color])}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

// Tenant Card Component
interface TenantCardProps {
  tenant: Tenant;
}

function TenantCard({ tenant }: TenantCardProps) {
  const initials = `${tenant.firstName[0]}${tenant.lastName[0]}`;
  const fullName = `${tenant.firstName} ${tenant.lastName}`;

  const statusColors = {
    Active: 'bg-green-100 text-green-700',
    Applicant: 'bg-amber-100 text-amber-700',
    Former: 'bg-gray-100 text-gray-600',
  };

  return (
    <div className="group flex items-center gap-4 rounded-xl bg-white p-4 shadow-sm transition-all hover:shadow-md">
      {/* Avatar */}
      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-primary-700 text-sm font-semibold text-white">
        {initials}
      </div>

      {/* Tenant Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900 group-hover:text-primary-600">
            {fullName}
          </h3>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium',
              statusColors[tenant.status as keyof typeof statusColors]
            )}
          >
            {tenant.status}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
          <div className="flex items-center gap-1">
            <Mail className="h-3.5 w-3.5" />
            <span className="truncate">{tenant.email}</span>
          </div>
          <div className="flex items-center gap-1">
            <Phone className="h-3.5 w-3.5" />
            <span>{tenant.phone}</span>
          </div>
        </div>
      </div>

      {/* Unit Info */}
      {tenant.currentUnitId && (
        <div className="hidden text-right sm:block">
          <div className="flex items-center justify-end gap-1 text-sm font-medium text-gray-900">
            <Home className="h-3.5 w-3.5 text-gray-400" />
            Assigned
          </div>
        </div>
      )}

      {/* Move-in Date */}
      {tenant.moveInDate && tenant.status === 'Active' && (
        <div className="hidden text-right lg:block">
          <div className="flex items-center justify-end gap-1 text-sm text-gray-500">
            <Calendar className="h-3.5 w-3.5" />
            Since {new Date(tenant.moveInDate).toLocaleDateString('en-US', {
              month: 'short',
              year: 'numeric',
            })}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        {tenant.currentUnitId ? (
          <Button variant="outline" size="sm" className="hidden sm:flex" onClick={() => toast.success('View Lease clicked (Coming Soon)')}>
            View Lease
          </Button>
        ) : (
          <Button variant="outline" size="sm" className="hidden sm:flex text-primary-600 border-primary-200 hover:bg-primary-50">
            Assign Unit
          </Button>
        )}

        <button className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
          <MoreVertical className="h-4 w-4" />
        </button>

        <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-primary-600" />
      </div>
    </div>
  );
}
